import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Warning from '@mui/icons-material/Warning'
import {
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import {
  Ledger,
  RetailerAccount,
  Brand,
} from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { formatWithoutCents } from '@src/lib/Utils'
import { currentRetailerAccountSelector } from '@src/stores/app/selectors'

import { BRAND_FUNDS_EXPIRING_SOON } from './constants'

export interface BrandLedgerGrouping {
  brandUuid: string
  ledgers: Ledger[]
}

const WalletBrandFunds: React.FC<
  React.PropsWithChildren<{ brandFunds: BrandLedgerGrouping[] }>
> = (props) => {
  const currentRetailerAccount: RetailerAccount = useSelector(
    currentRetailerAccountSelector,
  )

  const brandsByUuid = useMemo(() => {
    const brandsByUuid: Record<string, Brand> = {}

    currentRetailerAccount.retailers?.forEach((x) => {
      const brand = x.brand

      if (brand && brand.uuid) {
        brandsByUuid[brand.uuid] = brand
      }
    })

    return brandsByUuid
  }, [currentRetailerAccount.retailers])

  return (
    <>
      {props.brandFunds.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" padding={4}>
          {I18n.t('wallet.brandFunds.noneAvailable')}
        </Typography>
      ) : undefined}

      <List dense disablePadding component="div">
        {props.brandFunds.map((group, index, list) => {
          // TODO This is exactly the type of thing the graph should be doing
          // for us, but our API provides no way to look up a brand by its uuid.
          const brand = brandsByUuid[group.brandUuid]

          if (!brand) {
            return null
          }

          return (
            <BrandLedgerGroupingListItem
              key={group.brandUuid}
              group={group}
              brand={brand}
              divider={index < list.length - 1}
            />
          )
        })}
      </List>
    </>
  )
}

const BrandLedgerGroupingListItem: React.FC<
  React.PropsWithChildren<{
    group: BrandLedgerGrouping
    divider: boolean
    brand: Brand
  }>
> = (props) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const total = props.group.ledgers.reduce((memo, x) => {
    return memo + Number(x.balance)
  }, 0)
  const hasMultiple = props.group.ledgers.length > 1
  const areAnyLedgersExpiringSoon = props.group.ledgers.some((x) =>
    isLedgerExpiringSoon(x),
  )

  const expiringSoon = (
    <>
      <Warning
        color="inherit"
        fontSize="small"
        sx={{ verticalAlign: 'bottom' }}
      />{' '}
      {I18n.t('wallet.brandFunds.expiringSoon')}
    </>
  )

  const summary = areAnyLedgersExpiringSoon ? expiringSoon : undefined

  return (
    <>
      <ListItem
        disableGutters
        component="div"
        divider={props.divider}
        // `button` is deprecated and has weird typings, such that a `boolean`
        // isn't valid. It wants literally only true or false.
        // @ts-expect-error
        button={hasMultiple}
        onClick={
          hasMultiple
            ? () => {
                setIsExpanded((prev) => !prev)
              }
            : undefined
        }
      >
        <ListItemAvatar>
          <Avatar
            src={props.brand.logo_url || ''}
            variant="square"
            sx={{ '> img': { objectFit: 'contain' } }}
          />
        </ListItemAvatar>

        <ListItemText
          primary={props.brand.name}
          secondary={summary}
          secondaryTypographyProps={{
            color: 'error.main',
          }}
        />

        <Stack direction="row" alignItems="center">
          <Typography color="text.secondary" variant="body2">
            {formatWithoutCents.format(total / 100)}
          </Typography>

          {hasMultiple ? (
            isExpanded ? (
              <ExpandLess />
            ) : (
              <ExpandMore />
            )
          ) : undefined}
        </Stack>
      </ListItem>

      {isExpanded ? (
        <List component="div" disablePadding dense>
          {props.group.ledgers.map((ledger, index, list) => {
            return (
              <ListItem
                key={ledger.id}
                component="div"
                divider={index < list.length - 1}
                disableGutters
                disablePadding
              >
                <ListItemText
                  inset
                  primary={formatWithoutCents.format(
                    Number(ledger.balance) / 100,
                  )}
                  secondary={
                    isLedgerExpiringSoon(ledger) ? expiringSoon : undefined
                  }
                  secondaryTypographyProps={{ color: 'error.main' }}
                />
              </ListItem>
            )
          })}
        </List>
      ) : undefined}
    </>
  )
}

function isLedgerExpiringSoon(ledger: Ledger) {
  if (!ledger.expiration_date) {
    return false
  }

  return (
    new Date(ledger.expiration_date).valueOf() - new Date().valueOf() <=
    BRAND_FUNDS_EXPIRING_SOON
  )
}

export default WalletBrandFunds
